import { toast } from 'react-toastify';

/**
 * Clase para manejar notificaciones relacionadas con autenticación
 * Incluye sistema de deduplicación para evitar múltiples notificaciones
 */
class AuthNotifications {
  // Tipos de notificaciones
  static TYPES = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info'
  };

  // Control de notificaciones ya mostradas (para deduplicación)
  static _shownNotifications = new Map();
  static _sessionExpiredShown = false;
  static _redirectInProgress = false;
  
  // Tiempo de cooldown para notificaciones duplicadas (ms)
  static COOLDOWN_MS = 5000;

  // Eventos de autenticación
  static EVENTS = {
    // Eventos de login
    LOGIN_SUCCESS: 'login_success',
    LOGIN_FAILED: 'login_failed',
    
    // Eventos de logout
    LOGOUT_SUCCESS: 'logout_success',
    LOGOUT_FAILED: 'logout_failed',
    
    // Eventos de refresh token
    REFRESH_TOKEN_SUCCESS: 'refresh_token_success',
    REFRESH_TOKEN_FAILED: 'refresh_token_failed',
    
    // Eventos de sesión
    SESSION_EXPIRED: 'session_expired',
    SESSION_INVALID: 'session_invalid',
    
    // Eventos de seguridad
    SECURITY_ERROR: 'security_error',
    CSRF_ERROR: 'csrf_error',
    SUSPICIOUS_ACTIVITY: 'suspicious_activity'
  };

  // Configuración de notificaciones por evento
  static EVENT_CONFIG = {
    [AuthNotifications.EVENTS.LOGIN_SUCCESS]: {
      position: "top-right",
      autoClose: 2000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    },
    [AuthNotifications.EVENTS.LOGIN_FAILED]: {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    },
    [AuthNotifications.EVENTS.SECURITY_ERROR]: {
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false
    }
    // Otros eventos usan la configuración por defecto
  };

  /**
   * Verifica si una notificación ya fue mostrada recientemente
   * @param {string} event - Evento de autenticación
   * @returns {boolean} - true si ya fue mostrada recientemente
   */
  static _wasRecentlyShown(event) {
    const lastShown = AuthNotifications._shownNotifications.get(event);
    if (!lastShown) return false;
    
    const elapsed = Date.now() - lastShown;
    return elapsed < AuthNotifications.COOLDOWN_MS;
  }

  /**
   * Marca una notificación como mostrada
   * @param {string} event - Evento de autenticación
   */
  static _markAsShown(event) {
    AuthNotifications._shownNotifications.set(event, Date.now());
  }

  /**
   * Limpia el estado de notificaciones (útil después de login exitoso)
   */
  static reset() {
    AuthNotifications._shownNotifications.clear();
    AuthNotifications._sessionExpiredShown = false;
    AuthNotifications._redirectInProgress = false;
  }

  /**
   * Muestra una notificación con deduplicación
   * @param {string} event - Evento de autenticación
   * @param {string} type - Tipo de notificación (success, error, warning, info)
   * @param {string} message - Mensaje a mostrar
   * @param {Object} options - Opciones adicionales
   * @param {boolean} options.force - Forzar mostrar aunque esté en cooldown
   */
  static notify(event, type, message, options = {}) {
    // Asegurarse de que el mensaje sea una cadena
    const safeMessage = typeof message === 'string' ? message : 
                        (message && message.toString ? message.toString() : 'Error desconocido');
    
    // Eventos críticos de sesión que solo deben mostrarse una vez
    const sessionEvents = [
      AuthNotifications.EVENTS.SESSION_EXPIRED,
      AuthNotifications.EVENTS.SESSION_INVALID,
      AuthNotifications.EVENTS.REFRESH_TOKEN_FAILED
    ];
    
    // Si es un evento de sesión expirada y ya se mostró, no mostrar de nuevo
    if (sessionEvents.includes(event)) {
      if (AuthNotifications._sessionExpiredShown && !options.force) {
        // Solo registrar en consola, no mostrar toast
        //console.log(`[Auth] Notificación suprimida (ya mostrada): ${event}`);
        return;
      }
      // Marcar que ya se mostró una notificación de sesión expirada
      AuthNotifications._sessionExpiredShown = true;
    }
    
    // Para otros eventos, verificar cooldown
    if (!options.force && AuthNotifications._wasRecentlyShown(event)) {
      //console.log(`[Auth] Notificación en cooldown: ${event}`);
      return;
    }
    
    // Marcar como mostrada
    AuthNotifications._markAsShown(event);
    
    // Obtener configuración específica del evento o usar la predeterminada
    const config = AuthNotifications.EVENT_CONFIG[event] || {
      position: "top-right",
      autoClose: 3000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true
    };
    
    // Añadir toastId para evitar duplicados de react-toastify
    config.toastId = `auth-${event}`;
    
    // Mostrar notificación según el tipo
    switch (type) {
      case AuthNotifications.TYPES.SUCCESS:
        toast.success(safeMessage, config);
        break;
      case AuthNotifications.TYPES.ERROR:
        toast.error(safeMessage, config);
        break;
      case AuthNotifications.TYPES.WARNING:
        toast.warning(safeMessage, config);
        break;
      case AuthNotifications.TYPES.INFO:
        toast.info(safeMessage, config);
        break;
      default:
        toast(safeMessage, config);
    }
  }

  /**
   * Notifica sesión expirada y maneja redirección
   * Solo muestra una notificación y redirige una vez
   */
  static notifySessionExpiredAndRedirect() {
    if (AuthNotifications._redirectInProgress) {
      //console.log('[Auth] Redirección ya en progreso, ignorando...');
      return;
    }
    
    AuthNotifications._redirectInProgress = true;
    
    // Mostrar una única notificación
    AuthNotifications.notify(
      AuthNotifications.EVENTS.SESSION_EXPIRED,
      AuthNotifications.TYPES.ERROR,
      "Tu sesión ha expirado. Serás redirigido al login.",
      { force: true }
    );
    
    // Redirigir después de un breve retraso
    setTimeout(() => {
      window.location.href = "/login";
    }, 1500);
  }
}

export default AuthNotifications;
